import React, {useEffect, useRef, useState} from 'react';

import {MinimalFolder} from 'constants/FolderConstants';
import notifications from 'notifications';
import * as RegistryUtil from 'registry/RegistryUtil';
import {useDebounce} from 'ui-library/hooks/useDebounce';
import {Size} from 'ui-library/inputs/InputConstants';
import TextInput from 'ui-library/inputs/TextInput';
import SelectTree from 'ui-library/select-tree/SelectTree';
import {TreeItem} from 'ui-library/select-tree/SelectTreeConstants';
import {assertNever} from 'util/Types';

import * as FileSelectorUtil from './FileSelectorUtil';
import FolderDAO from './FolderDAO';
import usePaginatedFolderTree, {PaginatedFolderOptions} from './usePaginatedFolderTree';

export interface FolderSelectTreeProps {
  allowNone?: boolean;
  className?: string;
  /** Folders created while the picker is open, shown immediately without refetching earlier pages. */
  createdFolders?: Array<MinimalFolder>;
  folderIds: Array<string>;
  onChangeSelectedFolders: (folderIds: Array<string>) => void;
  folders?: Array<MinimalFolder>;
  includeHiddenProjects?: boolean;
  multiple: boolean;
  paginated?: PaginatedFolderOptions;
  submitHandler?: () => void;
}

type FolderTreeMode = 'eager' | 'paginated-browse' | 'paginated-search';

function getFolderTreeMode(paginated: PaginatedFolderOptions | undefined, query: string): FolderTreeMode {
  if (paginated == null) {
    return 'eager';
  }
  return query === '' ? 'paginated-browse' : 'paginated-search';
}

function mergeTreeItems(...itemGroups: Array<Array<TreeItem>>): Array<TreeItem> {
  return [...new Map(itemGroups.flat().map((item) => [item.id, item])).values()];
}

function getCreatedFolderTreeItems(
  folders: Array<MinimalFolder>,
  createdFolders: Array<MinimalFolder>
): {ancestorItems: Array<TreeItem>; ancestorIds: ReadonlySet<string>; leafItems: Array<TreeItem>} {
  const createdFolderIds = new Set(createdFolders.map((folder) => folder.api_identifier));
  const ancestorIds = new Set<string>();
  for (const folder of createdFolders) {
    let parent = folder.parent;
    while (parent != null) {
      ancestorIds.add(parent.api_identifier);
      parent = parent.parent;
    }
  }
  const folderItems = FileSelectorUtil.foldersToSelectTreeItems(folders);
  return {
    ancestorItems: folderItems.filter((item) => ancestorIds.has(item.id)),
    ancestorIds,
    leafItems: folderItems.filter((item) => createdFolderIds.has(item.id) && !ancestorIds.has(item.id)),
  };
}

export default function FolderSelectTree({
  includeHiddenProjects = false,
  allowNone = false,
  ...props
}: FolderSelectTreeProps): JSX.Element {
  const [query, setQuery] = useState('');
  const trimmedQuery = query.trim();
  const debouncedQuery = useDebounce(trimmedQuery, 500);
  const [searchFolders, setSearchFolders] = useState<Array<MinimalFolder> | null>(null);
  const paginatedTree = usePaginatedFolderTree(props.paginated);
  const mode = getFolderTreeMode(props.paginated, trimmedQuery);
  const previousModeRef = useRef<FolderTreeMode | null>(null);
  const paginatedLevel = props.paginated?.level;
  const onPaginatedFoldersLoaded = props.paginated?.onFoldersLoaded;
  const {syncBrowsePropsFromCache} = paginatedTree;

  useEffect(() => {
    const previousMode = previousModeRef.current;
    previousModeRef.current = mode;
    // Expanding a folder loads its children into the cache, but not into the browse tree props that
    // SelectTree reads. Search uses a separate folder list, so we only need to catch up when the user
    // clears the filter and browse mode remounts the tree.
    if (previousMode === 'paginated-search' && mode === 'paginated-browse') {
      syncBrowsePropsFromCache();
    }
  }, [mode, syncBrowsePropsFromCache]);

  useEffect(() => {
    if (mode !== 'paginated-search' || searchFolders != null || paginatedLevel == null) {
      return;
    }
    let canceled = false;
    FolderDAO.fetchAll({level: paginatedLevel})
      .done((loadedFolders: Array<MinimalFolder>) => {
        if (!canceled) {
          setSearchFolders(loadedFolders);
          onPaginatedFoldersLoaded?.(loadedFolders);
        }
      })
      .fail((error) => {
        if (!canceled) {
          setSearchFolders([]);
          notifications.notifyError(error);
        }
      });
    return () => {
      canceled = true;
    };
  }, [mode, onPaginatedFoldersLoaded, paginatedLevel, searchFolders]);

  let folders = props.folders ?? [];
  if (!includeHiddenProjects) {
    folders = folders.filter(({source}) => source && !source.hidden && !source.archived);
  }
  const hasRegistries = folders.some((folder) => RegistryUtil.isRegistry(folder.source));
  let items: Array<TreeItem>;
  let baseLeafItems: Array<TreeItem> | undefined;
  let treeQuery: string | undefined;
  let treeIsLoading: boolean | undefined;
  switch (mode) {
    case 'eager':
      // Existing callers provide the complete folder list, which SelectTree filters locally.
      items = FileSelectorUtil.foldersToSelectTreeItems(folders);
      baseLeafItems = undefined;
      treeQuery = debouncedQuery;
      treeIsLoading = debouncedQuery !== trimmedQuery;
      break;
    case 'paginated-browse': {
      // Fetch top-level pages and load each folder's children only when that folder is expanded.
      const createdFolderTreeItems = getCreatedFolderTreeItems(folders, props.createdFolders ?? []);
      items = mergeTreeItems(paginatedTree.items, createdFolderTreeItems.ancestorItems);
      baseLeafItems = mergeTreeItems(
        paginatedTree.baseLeafItems.filter((item) => !createdFolderTreeItems.ancestorIds.has(item.id)),
        createdFolderTreeItems.leafItems
      );
      treeQuery = undefined;
      treeIsLoading = undefined;
      break;
    }
    case 'paginated-search': {
      // Preserve typeahead behavior by fetching the complete list once and filtering it locally.
      const shouldShowSearchFolder = FileSelectorUtil.folderPickerPredicate({
        level: props.paginated?.level ?? 'read',
        showInventory: props.paginated?.showInventory ?? false,
        showNotebook: props.paginated?.showNotebook ?? false,
      });
      const visibleSearchFolders = (searchFolders ?? []).filter((folder) => shouldShowSearchFolder(folder));
      items = FileSelectorUtil.foldersToSelectTreeItems(visibleSearchFolders);
      baseLeafItems = undefined;
      treeQuery = debouncedQuery;
      treeIsLoading = searchFolders == null || debouncedQuery !== trimmedQuery;
      break;
    }
    default:
      throw new Error(`Unknown folder tree mode: ${assertNever(mode)}`);
  }
  const paginatedBrowseTree = mode === 'paginated-browse' ? paginatedTree : undefined;
  return (
    <div>
      <TextInput
        value={query}
        placeholder="Filter..."
        onChange={(event) => {
          setQuery(event.target.value);
        }}
        size={Size.SMALL}
      />
      <SelectTree
        // Search and browse supply different tree shapes, so their pagination state cannot be shared safely.
        key={mode}
        allowNone={allowNone}
        className={props.className}
        items={items}
        autoLoadMore={paginatedBrowseTree != null}
        baseLeafItems={baseLeafItems}
        isLoadedItemExpandable={paginatedBrowseTree?.isLoadedItemExpandable}
        multiple={props.multiple}
        itemTypeName="folders"
        selectAllText={hasRegistries ? 'All Projects and Registries' : 'All Projects'}
        selectedIds={props.folderIds}
        onChangeSelectedIds={props.onChangeSelectedFolders}
        submitHandler={props.submitHandler}
        shouldExpandNewItems={false}
        shouldExpandOnSelect={paginatedBrowseTree != null}
        shouldNotLoadMoreOnMount={mode !== 'eager'}
        loadMore={paginatedBrowseTree?.loadMore}
        moreRootItems={paginatedBrowseTree?.moreRootItems ?? false}
        isLoadingRootItems={paginatedBrowseTree?.isLoadingRootItems ?? false}
        onLoadMoreRootItems={paginatedBrowseTree?.onLoadMoreRootItems}
        query={treeQuery}
        isLoading={treeIsLoading}
      />
    </div>
  );
}
